import { css } from "@emotion/css";
import styled from "@emotion/styled";
import { Button, Chip, Flex, Pagination, Select, useMantineColorScheme } from "@mantine/core";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Column, SortColumn } from "react-data-grid";
import DataGrid, { FormatterProps, SelectColumn } from "react-data-grid";

import { mutatePalletErps } from "@/api/palletErp/usePalletErpQuery";
import { productionPerformances } from "@/api/productionPerformance/useProductionPerformanceQuery";
import { EquipmentDetailForm } from "@/features/equipment/components/form/EquipmentDetailForm";
import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { ChipsBox } from "@/features/standard/Chips/ChipsBox";
import customAlert from "@/features/ui/alert/alert";
import { CalendarDate } from "@/features/ui/Calendar";
import { DetailLink } from "@/features/ui/detail/DetailLink";
import { UsersDetailForm } from "@/features/users/components/form/UsersDetailForm";
import { usePub, useSub } from "@/hooks";
import { useLoader } from "@/hooks/useLoader";
import DefaultInstance from "@/instance/axios";
import { theme } from "@/styles/theme";
import { dataStyle, excelDownLoad, headerStyle } from "@/utils/excelDownLoad";
import { setToLocaleString } from "@/utils/unitMark";
import {
  DefaultApiErpSyncDeleteErpSplitWorkRecordPostRequest,
  DefaultApiErpSyncPushErpSplitWorkRecordPostRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import {
  SplitLotGet200ResponseRowsInner,
  SplitLotGet200ResponseRowsInnerDefectsInner,
  WorkLogsGet200ResponseRowsInner,
} from "@sizlcorp/sizl-api-document/dist/models/src/model";
import { IconFileSpreadsheet, IconFolder, IconFolderCancel } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { debounce } from "es-toolkit";
import { useTranslation } from "react-i18next";
import { ConsumeDetailForm } from "../consumeDetail/consumeDetailForm";
import { DefectDetailForm } from "../defectDetail/defectDetailForm";
import { ProductionPerformanceTotalTable } from "../totalTable";

interface ColorThemeProps {
  isDarkMode: boolean;
}

interface WorkLogTableProps {
  dates: CalendarDate;
  filterQueries: { [key: string]: string };
}
interface Query {
  $and: Array<{
    createdAt?: {
      $gte: string | undefined;
      $lte: string | undefined;
    };
    routingOutsourceId?: {
      $eq: null; // 외주가 아닐때 eq
    };
    checkErpSync?: {
      $or: [{ $eq: null }, { $eq: "D" }];
    };
    [key: string]: any;
  }>;
}
interface SearchProps {
  pageSize: string;
}

export const ProductionPerformanceTable = (params: WorkLogTableProps) => {
  const { dates, filterQueries } = params;
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const { setLoading, LoadingOverlay, loading } = useLoader();
  const isDarkMode: boolean = colorScheme === "light";
  const publish = usePub();
  const { t } = useTranslation();
  const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([]);
  const [formatterProps, setFormatterProps] = useState<
    FormatterProps<WorkLogsGet200ResponseRowsInner, unknown> | undefined
  >();
  const [selectedRows, setSelectedRows] = useState((): ReadonlySet<any> => new Set());
  const [activePage, setPage] = useState(1);
  const [filterErpSync, setFilterErpSync] = useState(false);
  const [selectedRowsArray, setSelectedRowsArray] = useState<any[]>([]);
  const [pageSize, setPageSizeState] = useState<number>();
  const [debouncedFilter, setDebouncedFilter] = useState<{ [key: string]: string }>(
    () => filterQueries,
  );
  const prevDatesRef = useRef<CalendarDate>(dates);

  useEffect(() => {
    setSelectedRowsArray(Array.from(selectedRows));
  }, [selectedRows]);

  const [query, setQuery] = useState<Query>({
    $and: [
      {
        createdAt: {
          $gte: dates[0]?.toISOString(),
          $lte: dates[1]?.toISOString(),
        },
      },
      {
        routingOutsourceId: { $eq: null },
      },
      {
        checkErpSync: {
          $or: [{ $eq: null }, { $eq: "D" }],
        },
      },
    ],
  });

  const { openModal } = useModal();

  // debounce 함수 정의
  const debouncedFilterChange = useCallback(
    debounce((newFilterQueries) => {
      setDebouncedFilter(newFilterQueries);
    }, 500), // 1000ms 지연 시간
    [],
  );

  // filterQueries가 변경될 때 debounce 함수 호출
  useEffect(() => {
    debouncedFilterChange(filterQueries);
  }, [filterQueries, debouncedFilterChange]);

  // filterQueries가 변경되면 페이지를 1로 설정
  useEffect(() => {
    if (debouncedFilter) {
      setPage(1);
    }
  }, [debouncedFilter]);

  // 페이지가 변경되면 선택된 행 초기화
  useEffect(() => {
    if (activePage || debouncedFilter) {
      setSelectedRows(new Set());
    }
  }, [activePage, debouncedFilter]);

  useEffect(() => {
    // 이전 dates와 비교
    const prevDates = JSON.stringify(prevDatesRef.current);
    const currentDates = JSON.stringify(dates);
    if (prevDates !== currentDates) {
      setSelectedRows(new Set()); // 날짜가 변경되었을 때만 초기화
    }
    prevDatesRef.current = dates;
  }, [dates]);

  useEffect(() => {
    setQuery((prevQuery) => {
      // 기존 조건에서 filterQueries 관련 조건 제거
      const newAndConditions = prevQuery.$and
        .filter((condition) => !Object.keys(debouncedFilter).includes(Object.keys(condition)[0]))
        .map((item) => {
          if (item.createdAt) {
            return {
              ...item,
              createdAt: {
                $gte: dates[0]?.toISOString(),
                $lte: dates[1]?.toISOString(),
              },
            };
          }
          return item;
        });

      // filterQueries 추가
      Object.entries(debouncedFilter).forEach(([key, value]) => {
        if (value) {
          newAndConditions.push({
            [key]: { $iLike: "%" + value + "%" },
          });
        }
      });

      // checkErpSync 조건이 이미 있는지 확인
      const hasCheckErpSync = newAndConditions.some(
        (condition) => condition.checkErpSync && condition.checkErpSync.$or,
      );

      // checkErpSync 조건이 없고 filterErpSync가 true인 경우에만 추가
      if (filterErpSync && !hasCheckErpSync) {
        newAndConditions.push({
          checkErpSync: {
            $or: [{ $eq: null }, { $eq: "D" }],
          },
        });
      }

      // checkErpSync 조건이 있고 filterErpSync가 false인 경우 제거
      if (!filterErpSync && hasCheckErpSync) {
        return {
          $and: newAndConditions.filter(
            (condition) => !(condition.checkErpSync && condition.checkErpSync.$or),
          ),
        };
      }

      // 빈 객체 제거
      return { $and: newAndConditions.filter((condition) => Object.keys(condition).length > 0) };
    });
  }, [dates, filterErpSync, debouncedFilter]);

  const {
    data: productionPerformanceData,
    refetch,
    isLoading,
  } = useQuery(
    productionPerformances.get({
      query: query,
      page: activePage,
      pageSize: pageSize,
      sort: "-createdAt",
    }),
  );

  // erp 전송
  const { mutate: postERP } = useMutation(
    (params: DefaultApiErpSyncPushErpSplitWorkRecordPostRequest) =>
      mutatePalletErps
        .postERP(params)
        .mutationFn(params as DefaultApiErpSyncPushErpSplitWorkRecordPostRequest | any),
    {
      onMutate: () => {
        setLoading(true);
      },
      onSuccess: (res) => {
        if (res.data.success && res.data.success.length) {
          const workStr = res.data.success.join(", ");
          customAlert(
            t("performance.erpSuccessMessage", { workStr: workStr }),
            t("performance.erp"),
            "info",
          );
        }

        if (res.data.fail && res.data.fail.length) {
          const workStr = res.data.fail.join(", ");
          customAlert(
            t("performance.erpFailMessage", { workStr: workStr }),
            t("performance.erp"),
            "red",
          );
        }
        setLoading(false);
        setSelectedRows(new Set());
        setSelectedRowsArray([]);
        refetch();
      },
      onError: (err) => {
        setLoading(false);
      },
    },
  );

  // erp 전송 취소
  const { mutate: cancelERP } = useMutation(
    (params: DefaultApiErpSyncDeleteErpSplitWorkRecordPostRequest) =>
      mutatePalletErps
        .cancelERP(params)
        .mutationFn(params as DefaultApiErpSyncDeleteErpSplitWorkRecordPostRequest | any),
    {
      onMutate: () => {
        setLoading(true);
      },
      onSuccess: (res) => {
        if (res.data.success && res.data.success.length) {
          const workStr = res.data.success.join(", ");
          customAlert(
            t("performance.erpCancelSuccessMessage", { workStr: workStr }),
            t("performance.erpCancel"),
            "info",
          );
        }

        if (res.data.fail && res.data.fail.length) {
          const workStr = res.data.fail.join(", ");
          customAlert(
            t("performance.erpCancelFailMessage", { workStr: workStr }),
            t("performance.erpCancel"),
            "red",
          );
        }
        setLoading(false);
        setSelectedRows(new Set());
        setSelectedRowsArray([]);
        refetch();
      },
      onError: (err) => {
        setLoading(false);
      },
    },
  );

  useEffect(() => {
    if (isLoading) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [isLoading]);

  const rows: readonly SplitLotGet200ResponseRowsInner[] =
    productionPerformanceData?.data.rows ?? [];
  const selectedRowsRef = useRef<ReadonlySet<any>>(new Set());

  const columns: readonly Column<SplitLotGet200ResponseRowsInner | any>[] = [
    {
      ...SelectColumn,
      width: 70,
      maxWidth: 500,
      resizable: true,
      headerCellClass: css`
        & > * {
          justify-content: flex-start;
          padding-left: 24px;
        }
      `,
      cellClass: css`
        .rdg-checkbox-label {
          padding-left: 24px;
        }
      `,
    },
    {
      key: "checkErpSync",
      name: t("performance.checkErp"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <div>
            {row.checkErpSync === "A"
              ? t("performance.syncComplete")
              : row.checkErpSync === "D"
                ? t("performance.syncCancel")
                : t("performance.syncWait")}
          </div>
        );
      },
    },
    {
      key: "createdAt",
      name: t("work.workDay"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <div>
            {row.createdAt !== null ? dayjs(row.createdAt).format("YYYY-MM-DD HH:mm:ss") : "-"}
          </div>
        );
      },
    },
    {
      key: "erpSyncedAt",
      name: t("performance.erpSyncedAt"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <div>
            {row.erpSyncedAt !== null ? dayjs(row.erpSyncedAt).format("YYYY-MM-DD HH:mm:ss") : "-"}
          </div>
        );
      },
    },
    {
      key: "equipmentName",
      name: t("equipment.name"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <DetailLink
            onClick={() =>
              openModal(
                <EquipmentDetailForm equipmentCode={row.equipmentCode} />,
                null,
                t("common.detail", { item: t("equipment.equipment") }),
              )
            }
          >
            {row.equipmentName}
          </DetailLink>
        );
      },
    },
    {
      key: "lotName",
      name: "LOT NO",
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <DetailLink
            onClick={() => openModal(<ConsumeDetailForm consumeData={row.consumeLogs} />, null, "")}
          >
            {row?.lotName}
          </DetailLink>
        );
      },
    },
    {
      key: "erpWorkOrderNo",
      name: t("workLog.workId"),
      sortable: true,
      resizable: true,
    },
    {
      key: "itemName",
      name: t("item.name"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <DetailLink
            onClick={() =>
              openModal(
                <ItemsDetailForm itemCode={row.itemCode} />,
                null,
                t("common.detail", { item: t("item.item") }),
              )
            }
          >
            {row.itemName}
          </DetailLink>
        );
      },
    },
    {
      key: "itemSpec",
      name: t("item.spec"),
      sortable: true,
      resizable: true,
    },
    {
      key: "productionSum",
      name: t("summary.todoQuantity"),
      sortable: true,
      resizable: true,
      cellClass: css`
        justify-content: flex-end;
      `,
    },
    {
      key: "totalQuantity",
      name: t("workLog.totalQuantity"),
      sortable: true,
      resizable: true,
      cellClass: css`
        justify-content: flex-end;
      `,
    },
    {
      key: "defects",
      name: t("work.defectQuantity"),
      sortable: true,
      resizable: true,
      cellClass: css`
        justify-content: flex-end;
      `,
      formatter: ({ row }) => {
        const totalDefects =
          row.defects?.reduce(
            (sum: any, defect: SplitLotGet200ResponseRowsInnerDefectsInner) =>
              sum + defect.defect_sum,
            0,
          ) || 0;
        return (
          <DetailLink
            onClick={() => openModal(<DefectDetailForm defectData={row.defects} />, null, "")}
          >
            <span>{setToLocaleString(totalDefects)}</span>
          </DetailLink>
        );
      },
    },
    {
      key: "prevSplitLotCreatedAt",
      name: t("kpi.workStartTime"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <div>
            {row.prevSplitLotCreatedAt !== null
              ? dayjs(row.prevSplitLotCreatedAt).format("YYYY-MM-DD HH:mm:ss")
              : "-"}
          </div>
        );
      },
    },
    {
      key: "createdAtEnd",
      name: t("kpi.workEndTime"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <div>
            {row.createdAt !== null ? dayjs(row.createdAt).format("YYYY-MM-DD HH:mm:ss") : "-"}
          </div>
        );
      },
    },
    {
      key: "timeDifference.hour",
      name: t("performance.workTime"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <div>
            {[
              row.timeDifference?.days ? `${row.timeDifference.days}${t("performance.day")}` : "",
              row.timeDifference?.hours
                ? `${row.timeDifference.hours}${t("performance.time")}`
                : "",
              row.timeDifference?.minutes
                ? `${row.timeDifference.minutes}${t("performance.minute")}`
                : "",
            ].join("")}
          </div>
        );
      },
    },
    {
      key: "creatorUserCode",
      name: t("workLog.worker"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <DetailLink
            onClick={() => openModal(<UsersDetailForm UserCode={row?.creatorUserCode} />, null, "")}
          >
            {row?.creatorUserCode}
          </DetailLink>
        );
      },
    },
    {
      key: "itemCode",
      name: t("item.code"),
      sortable: true,
      resizable: true,
    },
  ];

  useSub(
    "excelDownLoad",
    async () => {
      try {
        setLoading(true);
        const response = await DefaultInstance.splitLotFindPost({
          query: JSON.stringify(query),
          sort: "-createdAt",
        });

        const data = response.data;

        const headerRow = [
          { v: "전송여부", t: "s", s: headerStyle },
          { v: "작업일", t: "s", s: headerStyle },
          { v: "전송일시", t: "s", s: headerStyle },
          { v: "설비명", t: "s", s: headerStyle },
          { v: "LOTNO", t: "s", s: headerStyle },
          { v: "작업지시번호", t: "s", s: headerStyle },
          { v: "품목명", t: "s", s: headerStyle },
          { v: "규격", t: "s", s: headerStyle },
          { v: "생산수량", t: "s", s: headerStyle },
          { v: "양품수량", t: "s", s: headerStyle },
          { v: "불량수량", t: "s", s: headerStyle },
          { v: "작업시작 시간", t: "s", s: headerStyle },
          { v: "작업종료 시간", t: "s", s: headerStyle },
          { v: "작업 시간", t: "s", s: headerStyle },
          { v: "작업자", t: "s", s: headerStyle },
          { v: "품목코드", t: "s", s: headerStyle },
          { v: "자재소모내역", t: "s", s: headerStyle },
        ];
        const dataRows =
          data.map((item: SplitLotGet200ResponseRowsInner) => {
            const totalDefects =
              item.defects?.reduce(
                (sum: number, defect: SplitLotGet200ResponseRowsInnerDefectsInner) =>
                  sum + (defect.defect_sum ?? 0),
                0,
              ) ?? 0;

            return [
              {
                v:
                  item.checkErpSync === "A"
                    ? "전송완료"
                    : item.checkErpSync === "D"
                      ? "전송취소"
                      : "전송대기",
                t: "s",
                s: dataStyle,
              },
              {
                v: item.createdAt ? dayjs(item.createdAt).format("YYYY-MM-DD HH:mm:ss") : "-",
                t: "s",
                s: dataStyle,
              },
              {
                v: item.erpSyncedAt ? dayjs(item.erpSyncedAt).format("YYYY-MM-DD HH:mm:ss") : "-",
                t: "s",
                s: dataStyle,
              },
              { v: item.equipmentName ?? "", t: "s", s: dataStyle },
              { v: item.lotName ?? "", t: "s", s: dataStyle },
              { v: item.erpWorkOrderNo?.toString() ?? "", t: "s", s: dataStyle },
              { v: item.itemName ?? "", t: "s", s: dataStyle },
              { v: item.itemSpec?.toString() ?? "", t: "s", s: dataStyle },
              { v: setToLocaleString(item.productionSum) ?? "", t: "s", s: dataStyle },
              { v: setToLocaleString(item.totalQuantity) ?? "", t: "s", s: dataStyle },
              { v: setToLocaleString(totalDefects), t: "s", s: dataStyle },
              {
                v: item.prevSplitLotCreatedAt
                  ? dayjs(item.prevSplitLotCreatedAt).format("YYYY-MM-DD HH:mm:ss")
                  : "-",
                t: "s",
                s: dataStyle,
              },
              {
                v: item.createdAt ? dayjs(item.createdAt).format("YYYY-MM-DD HH:mm:ss") : "-",
                t: "s",
                s: dataStyle,
              },
              {
                v: `${item.timeDifference?.days?.toString() ?? "-"}일 ${
                  item.timeDifference?.hours?.toString() ?? "-"
                }시간 ${item.timeDifference?.minutes?.toString() ?? "-"}분`,
                t: "s",
                s: dataStyle,
              },
              { v: item.creatorUserCode ?? "", t: "s", s: dataStyle },
              { v: item.itemCode ?? "", t: "s", s: dataStyle },
              {
                v:
                  item.consumeLogs
                    ?.map((consume) => {
                      const lotName = consume?.lotName ? `로트명 - ${consume.lotName}` : "";
                      const itemCode = consume?.itemCode ? `품목코드 - ${consume.itemCode}` : "";
                      const quantity = consume?.quantity ? `수량 : ${consume.quantity}` : "";

                      // 값이 있는 항목만 필터링하여 문자열로 결합
                      return [lotName, itemCode, quantity].filter(Boolean).join(" ");
                    })
                    .join(" ") ?? "",
                t: "s",
                s: dataStyle,
              },
            ];
          }) ?? [];

        await excelDownLoad({
          headerRow,
          dataRows,
          colWidths: [
            120, 120, 120, 120, 120, 120, 120, 120, 120, 120, 120, 120, 120, 120, 120, 120, 280,
          ],
          fileName: "생산실적조회",
        });
      } catch (e) {
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [JSON.stringify(query)],
  );

  return (
    <WorkLogsTableWrapper>
      <LoadingOverlay />
      <GridWrapper isDarkMode={isDarkMode}>
        <TableWrapper>
          <RetrieveWrapper>
            <ChipsBox>
              <Chip
                variant="filled"
                size="sm"
                radius="xs"
                checked={filterErpSync || false}
                onClick={() => {
                  setFilterErpSync((prev) => !prev);
                  setSelectedRows(new Set());
                  setPage(1);
                }}
              >
                {t("performance.erpInquiry")}
              </Chip>
            </ChipsBox>
            <Flex gap="1rem">
              <Select
                data={[
                  { value: "5", label: t("page.5") },
                  { value: "10", label: t("page.10") },
                  { value: "15", label: t("page.15") },
                  { value: "20", label: t("page.20") },
                ]}
                value={pageSize?.toString() ?? "10"}
                onChange={(e) => {
                  setPageSizeState(Number(e));
                  setSelectedRows(new Set());
                }}
              />
              <Button
                leftIcon={<IconFolder />}
                onClick={() =>
                  postERP({
                    erpSyncPushErpSplitWorkRecordPostRequest: {
                      workLogIds: selectedRowsArray,
                    },
                  })
                }
                disabled={
                  rows.some(
                    (row) => selectedRowsArray.includes(row.id) && row.checkErpSync === "A",
                  ) || loading
                }
              >
                {t("performance.performanceSync")}
              </Button>
              <Button
                color="orange"
                leftIcon={<IconFolderCancel />}
                onClick={() =>
                  cancelERP({
                    erpSyncPushErpSplitWorkRecordPostRequest: {
                      workLogIds: selectedRowsArray,
                    },
                  })
                }
                disabled={
                  rows.some(
                    (row) => selectedRowsArray.includes(row.id) && row.checkErpSync !== "A",
                  ) || loading
                }
              >
                {t("performance.performanceSyncCancel")}
              </Button>
              <Button
                color="teal"
                rightIcon={<IconFileSpreadsheet />}
                onClick={() => {
                  publish("excelDownLoad");
                }}
              >
                {t("inventory.excelDownload")}
              </Button>
            </Flex>
          </RetrieveWrapper>
          <DataGrid
            columns={columns ?? []}
            rows={rows as any}
            rowHeight={40}
            rowKeyGetter={(row) => row.id}
            sortColumns={sortColumns}
            selectedRows={selectedRows}
            onSelectedRowsChange={(e) => {
              setSelectedRows(e);
              const newSelectedRows = new Set(selectedRowsRef.current);
              newSelectedRows.add(e);
              selectedRowsRef.current = newSelectedRows;
            }}
          />
          <ProductionPerformanceTotalTable selectedRows={selectedRows} />
        </TableWrapper>
        <PaginationWrapper>
          <Pagination
            onChange={setPage}
            value={activePage}
            total={productionPerformanceData?.data?.totalPages ?? 0}
            size="lg"
            radius="sm"
          />
        </PaginationWrapper>
      </GridWrapper>
    </WorkLogsTableWrapper>
  );
};

const WorkLogsTableWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
`;

const GridWrapper = styled.div<ColorThemeProps>`
  & *[role="grid"] {
    height: 440px;
    --rdg-background-color: ${(props) => (props.isDarkMode ? "white" : "none")};
    --rdg-header-background-color: ${(props) => (props.isDarkMode ? "white" : "none")};
    --rdg-color: ${(props) => (props.isDarkMode ? "black" : "white")};
    --rdg-row-hover-background-color: ${(props) =>
      props.isDarkMode ? "#f5f5f5" : theme?.colors?.gray?.[7]};
  }
  & *[role="columnheader"] {
    // color: #7d8fa9;
    font-size: 12px;
    font-family: Roboto;
    font-weight: 500;
    word-wrap: break-word;
    // border: none;
    box-shadow: none;
    display: flex;
    align-items: center;
  }
  & *[aria-colindex="1"] {
  }

  & *[role="row"] {
    height: 100px;
  }

  & *[role="gridcell"] {
    display: flex;
    align-items: center;
    // border-left: none;
    // border-right: none;
    box-shadow: none;

    & > * {
      justify-content: flex-start;
    }
  }
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RetrieveWrapper = styled.div`
  display: flex;
  flex-direction: row;
  padding-bottom: 10px;
  justify-content: space-between;
`;
const OptionBox = styled.fieldset`
  display: flex;
  gap: 10px;
  border: none;
`;
const OptionBtn = styled<any>(Button)`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
